import { EditType } from 'src/types/edit';
import { InsightSummaryPage } from 'src/types/pages/insights';

export const CLIENT_ENGAGEMENT: InsightSummaryPage = {
  page_id: 'insights-9',
  section_order: ['header', 'summary'],
  header: {
    visible: true,
    content: {
      title: 'Client Engagement',
      body: 'Mediators adapt to the changing needs of their clients by offering flexibility, creating welcoming environments, relying on their tried and true skill sets.',
      header_type: 'full',
    },
  },
  summary: {
    visible: true,
    content: {
      highlight_link_style: {
        default_color: '#11304B',
        active_color: '#48bf53',
      },
      paragraphs: [
        {
          label: 'Overview & Recommendations',
          text: 'The client engagement issue contains layers which were revealed in the facilitated conversations. Perhaps the most prominent concern was the challenge of cultivating and maintaining client <Embed getEmbedInfo={getEmbedInfo}>relationships-5248128</Embed> in the remote working environment. Some mediators expressed trepidation over the learning curve to employ remote meeting technology effectively. They found it challenging to overcome the impersonal feeling of an “online outreach” and difficult to maintain relationships when meeting in person was not an option. Other mediators saw the remote environment as an opportunity to <Embed getEmbedInfo={getEmbedInfo}>grow their practice-5249388</Embed> and cast a wider net in casework development and to solidify connections with clients.<br/><br/>Recommendations from conversations include:<br/><ul><li><Embed getEmbedInfo={getEmbedInfo}>Standardized training-5249391</Embed> on how to conduct an outreach.</li><li>Breakdown the key elements and desired outcomes from an outreach meeting.</li><li>How to overcome client hesitation, concerns and <Embed getEmbedInfo={getEmbedInfo}>unique needs-5248976</Embed> toward engaging in the virtual and hybrid meeting spaces.</li><li>Training on how to effectively service a client in the <Embed getEmbedInfo={getEmbedInfo}>hybrid meeting space-5249036</Embed>.</li><li>Suite of classes on how to train virtually and employ technology tools during in-person meetings.</li><li>Guidance on the <Embed getEmbedInfo={getEmbedInfo}>decision-making process-5249064</Embed> on whether to conduct client engagements remotely, in-person or in a hybrid space, and trust the mediator to make the decision on which meeting space is most appropriate within the guidelines.</li></ul>',
        },
        {
          label: 'Client Satisfaction',
          text: "In the midst of reconciling their new work and office realities, mediators never forget their focus on the clients they serve. Many participants, such as <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250355</Embed>, <Embed getEmbedInfo={getEmbedInfo}>Speaker TC-5249437</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker SA-5250354</Embed> describe the delicate dance involved in client management, many of whom express suspicion of virtual negations. To illustrate this, <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250355</Embed> recounted their experience of working with a “high maintenance” client who expressed great concern regarding virtual negotiation by stating that, “We can't trust the virtual platform. The parties aren't going to get together. We're not going to be able to get anything done.”<br/><br/>In response to Speaker BA, <Embed getEmbedInfo={getEmbedInfo}>Speaker SA-5250362</Embed> assured them that their strategy of meeting with every stakeholder individually and creating small steps to accomplish the work was similar to what they did with another client, “…we broke it down into little pieces… We went back and forth to caucus rooms. I took them one at a time and proved to them that they couldn't hear anything if they were in the caucus room, it was a very horizontal process.” Eventually, both BA’s and SA’s clients ended up trusting the process because they trusted the mediators, despite the obvious trepidation from the participants.",
        },
        {
          label: 'Effective Service',
          text: "As mediators explore the new terrain of virtual work and how they deliver services, the client is foremost on their mind as expressed by <Embed getEmbedInfo={getEmbedInfo}>Speaker MA-5248129</Embed>. Participants share stories of their experiences where their ability to deliver their best service felt compromised by virtual circumstances, particularly where there are mixed in-person and remote participant meetings. <Embed getEmbedInfo={getEmbedInfo}>Speaker WA-525062</Embed> recounts the awkward situation where all parties had moved to meeting in person but the agency had chosen not to allow travel at the time. Ultimately, the mediation failed and the mediator stated that, “It was an unsuccessful environment for me as a mediator, being there as a laptop sitting at the end of the table when everybody else was there in person.” <br/><br/>However, one mediator, <Embed getEmbedInfo={getEmbedInfo}>Speaker CC-5249078</Embed> has noted that the nature of their work can, at times, be quite arbitrary, “...you can work your ass off and not look like you succeeded, and do stretch things you never did before, and it was really one of the most accomplished mediations we were a part of. But it didn't look like we got the deal. And then there are others where you don't do much at all and you look like a superstar.” <br/><br/>Ultimately, mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker SC-5250630</Embed> observed that while some clients appreciate the convenience of virtual negotiations, there is also the sentiment expressed by <Embed getEmbedInfo={getEmbedInfo}>Speaker MA-5250454</Embed> that a multiplicity of options where clients may be served virtually and in a hybrid setting as well as the classic in-person format is desired, “I think that's pretty much going to be the wave of the future and I'm actually looking forward to it because I think it's going to be productive for all of us having the multiple formats and the multiple opportunities to meet the needs of our customers.”",
        },
        {
          label: 'Relationship Building',
          text: "Mediators worked tirelessly to cultivate authentic and productive relationships. So, when their ability to create connection with their clients is disrupted, they feel as <Embed getEmbedInfo={getEmbedInfo}>Speaker LE-5249100</Embed> does: “I was really concerned about how to read the mood of the clients and read the room in a way that you do in person naturally.” <br/><br/>Newer mediators like <Embed getEmbedInfo={getEmbedInfo}>Speaker KB-5250453</Embed> and <Embed getEmbedInfo={getEmbedInfo}>Speaker TB-5249397</Embed> expressed apprehension that the virtual environment places them at a disadvantage for creating rapport and building connections in comparison to more seasoned mediators who have built relationships over time, “I'm not here to make friends with any of the clients we work with. But coming in under five years, I don't really know the labor relations community as well as some of the people who have been here for 20 years when we went virtual.”",
        },
        {
          label: 'Meeting Spaces',
          text: "Even as mediators lament the loss of easy, in-person relationship building, they also marveled at the sheer scope, breadth, and variety of people and places that have intersected during the pandemic era of work. <Embed getEmbedInfo={getEmbedInfo}>Speaker BA-5250369</Embed> describes how they appreciate, “…this option of doing things virtually because I've done several virtual meetings with orchestra musicians…That’s kind of a piece where I can use my past expertise reaching out to colleagues, but I couldn't necessarily go to Chicago, or Indiana, or Arizona.” <br/><br/>And, <Embed getEmbedInfo={getEmbedInfo}>Speaker SA-5250341</Embed> describes what they consider to be remarkable opportunities to work with others, stating that, “In fact, in the first six months I have met and worked with on exciting projects, more colleagues in that first six months than I had in the entire seven years prior, and it's just exploded since then.” <br/><br/>Virtual platforms offered mediators intriguing new venues and formats to stretch their skills and explore new group dynamics. Referring to their surprise at how emotions emerged during online negotiations, <Embed getEmbedInfo={getEmbedInfo}>Speaker AB-5249107</Embed> shared their observation that even for online forums, “…the intensity definitely came through. I think in the virtual world, for me at least, silence was crickets, was, oh, okay. And I had the early COVID stages of inner struggle not to fill that void for everybody and take that on, because I wouldn't do that in person.”",
        },
      ],
    },
  },
  edit_metadata: {
    page_label: 'Client Engagement',
    page_id: 'insights-9',
    subroute: true,
    sections: [
      {
        section_label: 'Introduction',
        edit_blocks: [
          {
            block_label: 'Header',
            match: 'header.content',
            type: EditType.banner,
          },
        ],
      },
      {
        section_label: 'Highlight Link Colors',
        edit_blocks: [
          {
            block_label: 'Color',
            match: 'summary.content.highlight_link_style.default_color',
            type: EditType.color,
          },
          {
            block_label: 'Active Color',
            match: 'summary.content.highlight_link_style.active_color',
            type: EditType.color,
          },
        ],
      },
      {
        section_label: 'Paragraphs',
        update_section: {
          match: 'summary.content.paragraphs',
          label: 'Add Paragraph',
          empty: {
            label: 'Default Label',
            text: 'Default Text',
          },
          newSectionMetadata: {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: `summary.content.paragraphs[index].label`,
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: `summary.content.paragraphs[index].text`,
                type: EditType.embed_string,
              },
            ],
          },
        },
        sub_sections: [
          {
            section_label: 'Overview',
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[0].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[0].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[1].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[1].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[2].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[2].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[3].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[3].text',
                type: EditType.embed_string,
              },
            ],
          },
          {
            section_label: 'Insight',
            numbered: true,
            edit_blocks: [
              {
                block_label: 'Title',
                match: 'summary.content.paragraphs[4].label',
                type: EditType.string,
              },
              {
                block_label: 'Paragraph',
                match: 'summary.content.paragraphs[4].text',
                type: EditType.embed_string,
              },
            ],
          },
        ],
      },
    ],
  },
};
